import { ALL_CENTRES } from '@appTypes/auth';
import { AddOutlined } from '@mui/icons-material';
import { Box, Button, Divider, Drawer, MenuItem, TextField, Typography } from '@mui/material';
import { useAuthStore } from '@store/authStore';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ManageCentresDialog } from './ManageCentresDialog';
import { ManageMainCentresDialog } from './ManageMainCentresDialog';
import { NAV_ITEMS } from './navItems';
import { useScopeSwitcher } from './useScopeSwitcher';

interface MobileNavDrawerProps {
  open: boolean;
  onClose: () => void;
}

const SCOPE_LABEL_SX = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase' as const,
  mb: '6px',
  display: 'block',
};

export const MobileNavDrawer = ({ open, onClose }: MobileNavDrawerProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);

  const {
    isAdmin,
    sessions,
    activeMainCentres,
    activeCentres,
    mainCentreId,
    centreId,
    sessionId,
    currentMainCentreName,
    currentCentreName,
    changeMainCentre,
    changeCentre,
    changeSession,
  } = useScopeSwitcher();

  const visibleNavItems = NAV_ITEMS.filter((item) => !item.adminOnly || user?.role === 'admin');
  const [manageCentresOpen, setManageCentresOpen] = useState(false);
  const [manageMainCentresOpen, setManageMainCentresOpen] = useState(false);

  const handleNavClick = (route: string) => {
    navigate(route);
    onClose();
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '82vw', sm: 320 }, maxWidth: 360 } } }}
    >
      {/* Only mount contents while open to avoid needless renders. */}
      {open && (
        <Box sx={{ display: 'flex', flexDirection: 'column', py: '16px', px: '16px', gap: '18px' }}>
          {/* ── Session & Centre ── */}
          <Box>
            <Typography component="label" sx={(theme) => ({ ...SCOPE_LABEL_SX, color: theme.palette.grey[500] })}>
              Session
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={sessionId ?? ''}
              onChange={(e) => changeSession(e.target.value)}
              slotProps={{
                select: {
                  // A Select renders the chosen option's CHILDREN in the closed
                  // field, so without this the ✓ and "· current" would leak into
                  // the trigger. Render just the year there.
                  renderValue: (value) => sessions.find((s) => s._id === value)?.label ?? '',
                },
              }}
            >
              {sessions.map((s) => (
                <MenuItem key={s._id} value={s._id} sx={{ fontSize: '13px' }}>
                  {s.label}
                  {s.isCurrent && (
                    <Box component="span" sx={(theme) => ({ color: theme.palette.grey[400] })}>
                      {'\u00a0·\u00a0current'}
                    </Box>
                  )}
                  {/* Explicit margin rather than a literal space in the string —
                      a single space collapses tight against "· current". */}
                  {s._id === sessionId && (
                    <Box component="span" sx={{ ml: '8px' }}>✓</Box>
                  )}
                </MenuItem>
              ))}
              {sessions.length === 0 && (
                <MenuItem value="" disabled sx={{ fontSize: '13px' }}>No sessions</MenuItem>
              )}
            </TextField>

            <Typography component="label" sx={(theme) => ({ ...SCOPE_LABEL_SX, color: theme.palette.grey[500], mt: '14px' })}>
              Main Centre
            </Typography>
            {isAdmin ? (
              <TextField
                select
                size="small"
                fullWidth
                value={mainCentreId ?? ''}
                onChange={(e) => changeMainCentre(e.target.value)}
              >
                {activeMainCentres.map((m) => (
                  <MenuItem key={m._id} value={m._id} sx={{ fontSize: '13px' }}>
                    {m.name}
                  </MenuItem>
                ))}
                {activeMainCentres.length === 0 && (
                  <MenuItem value="" disabled sx={{ fontSize: '13px' }}>No main centres</MenuItem>
                )}
              </TextField>
            ) : (
              <Typography sx={(theme) => ({ fontSize: '14px', fontWeight: 600, color: theme.palette.grey[700], py: '6px' })}>
                {currentMainCentreName}
              </Typography>
            )}

            <Typography component="label" sx={(theme) => ({ ...SCOPE_LABEL_SX, color: theme.palette.grey[500], mt: '14px' })}>
              Centre
            </Typography>
            {isAdmin ? (
              <TextField
                select
                size="small"
                fullWidth
                value={centreId ?? ''}
                onChange={(e) => changeCentre(e.target.value)}
              >
                {activeCentres.map((c) => (
                  <MenuItem key={c._id} value={c._id} sx={{ fontSize: '13px' }}>
                    {c.name}
                  </MenuItem>
                ))}
                {activeCentres.length === 0 && (
                  <MenuItem value="" disabled sx={{ fontSize: '13px' }}>No centres</MenuItem>
                )}
                {/* Read-only combined view across this main centre. */}
                <MenuItem value={ALL_CENTRES} disabled={activeCentres.length === 0} sx={{ fontSize: '13px' }}>
                  All Centres — Dashboard &amp; Reports only
                </MenuItem>
              </TextField>
            ) : (
              <Typography sx={(theme) => ({ fontSize: '14px', fontWeight: 600, color: theme.palette.grey[700], py: '6px' })}>
                {currentCentreName}
              </Typography>
            )}
            {isAdmin && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mt: '10px' }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setManageMainCentresOpen(true)}
                  startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
                  sx={(theme) => ({
                    borderRadius: '8px', fontSize: 13, fontWeight: 600, textTransform: 'none',
                    px: '14px', py: '7px', color: theme.palette.grey[700], borderColor: theme.palette.grey[200],
                    '&:hover': { bgcolor: theme.palette.grey[50], borderColor: theme.palette.grey[300] },
                  })}
                >
                  Manage Main Centres
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setManageCentresOpen(true)}
                  startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
                  sx={(theme) => ({
                    bgcolor: theme.palette.primary.main, borderRadius: '8px', fontSize: 13, fontWeight: 600,
                    px: '14px', py: '7px', textTransform: 'none', boxShadow: 'none',
                    '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: 'none' },
                  })}
                >
                  Manage Centres
                </Button>
              </Box>
            )}
          </Box>

          <Divider />

          {/* ── Navigation ── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {visibleNavItems.map(({ label, Icon, route }) => {
              const active = pathname.startsWith(route);
              return (
                <Box
                  key={label}
                  onClick={() => handleNavClick(route)}
                  sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    px: '12px',
                    py: '11px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    bgcolor: active ? theme.palette.status.info.bg : 'transparent',
                    '&:hover': { bgcolor: active ? theme.palette.status.info.bg : theme.palette.grey[50] },
                  })}
                >
                  <Icon sx={(theme) => ({ fontSize: 19, color: active ? theme.palette.primary.main : theme.palette.grey[500] })} />
                  <Typography sx={(theme) => ({ fontSize: '14px', fontWeight: active ? 600 : 500, color: active ? theme.palette.primary.main : theme.palette.grey[700] })}>
                    {label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <ManageMainCentresDialog open={manageMainCentresOpen} onClose={() => setManageMainCentresOpen(false)} />
      <ManageCentresDialog open={manageCentresOpen} onClose={() => setManageCentresOpen(false)} />
    </Drawer>
  );
};
